/**
 * Snapshot the editor's scroll position (and the body textarea's caret, when
 * focused) so an inline reconcile — e.g. `reloadFromServer()` after a committed
 * agent write to the OPEN document/work-item — can REPLACE the editor content
 * WITHOUT moving the user's view or caret.
 *
 * Why this is needed: the V2 editor body is a CONTROLLED `<textarea>`. When the
 * reconcile calls `setBodyDraft(serverBody)` React reassigns the textarea's
 * `value`, which collapses the caret/selection (typically to the end) and — if
 * the new content is a different length — can shift the layout enough to move
 * the scrolling stage. The agent-write reconcile must be invisible to a user who
 * is reading/editing elsewhere in the doc, so we snapshot before the state
 * update and restore after the DOM has committed the new value.
 *
 * The scroll container is `.pn-editor-shell-stage` (the only `overflow-y: auto`
 * column in the editor shell; the body textarea itself auto-grows via
 * `field-sizing: content` and does not scroll). The caret is preserved ONLY when
 * the body textarea is the active element — a reconcile while the user is typing
 * is already guarded against by the `!dirty` check upstream, but if focus is in
 * the body for any other reason we keep the selection stable too.
 *
 * Restoration runs in a `requestAnimationFrame` (with a microtask fallback for
 * non-browser/jsdom environments) so it lands AFTER React has flushed the new
 * `value` into the DOM. Selection offsets are clamped to the new value length so
 * a shorter server body can never throw or land out of range.
 */

/** Capture the current editor viewport from a DOM node inside the editor. */
export interface EditorViewportSnapshot {
  /** Restore the captured scroll position + caret. Safe to call once. */
  restore: () => void;
}

/**
 * Snapshot the scroll/caret state of the editor that contains `anchor`.
 *
 * @param anchor Any element inside the editor shell — typically the body
 *   textarea (`bodyRef`) or the page section. Used to find the scrolling stage.
 * @returns a snapshot whose `restore()` re-applies the captured scrollTop and,
 *   if the body textarea was focused, its selection range. A no-op restore when
 *   there is no scroll container (e.g. unmounted).
 */
export function snapshotEditorViewport(
  anchor: HTMLElement | null | undefined
): EditorViewportSnapshot {
  const noop: EditorViewportSnapshot = { restore: () => {} };
  if (!anchor || typeof document === 'undefined') return noop;

  const stage = anchor.closest<HTMLElement>('.pn-editor-shell-stage');
  if (!stage) return noop;

  const scrollTop = stage.scrollTop;
  const scrollLeft = stage.scrollLeft;

  // Preserve the caret only when the focused element is a textarea/input INSIDE
  // this editor stage (the markdown body, the title field). A reconcile that
  // fires while the user's caret is elsewhere on the page must not steal focus
  // or move a selection that isn't in the editor.
  const active = document.activeElement;
  const focusedField =
    active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement
      ? active
      : null;
  const caret =
    focusedField && stage.contains(focusedField)
      ? {
          field: focusedField,
          start: focusedField.selectionStart,
          end: focusedField.selectionEnd,
          direction: focusedField.selectionDirection
        }
      : null;

  let restored = false;
  const apply = () => {
    if (restored) return;
    restored = true;
    stage.scrollTop = scrollTop;
    stage.scrollLeft = scrollLeft;
    if (caret) {
      const len = caret.field.value.length;
      const start = caret.start === null ? len : Math.min(caret.start, len);
      const end = caret.end === null ? len : Math.min(caret.end, len);
      try {
        caret.field.setSelectionRange(
          start,
          end,
          caret.direction ?? undefined
        );
      } catch {
        // setSelectionRange throws on detached / type-incompatible inputs —
        // the scroll restore above is the important part; swallow.
      }
    }
  };

  const restore = () => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(apply);
    } else {
      void Promise.resolve().then(apply);
    }
  };

  return { restore };
}
